import { useState } from 'react'
import { api } from '../../../lib/api'

export interface Candidate {
  key: 'a' | 'b'
  name: string
  photo_url: string
  color: string
  percentage: number
}

export interface ElectionState {
  title: string
  target_date: string
  show_countdown: boolean
  show_voting: boolean
  candidates: Candidate[]
}

interface Props {
  overlayId: string
  initial: Record<string, unknown>
}

export default function ElectionForm({ overlayId, initial }: Props) {
  const init = initial as unknown as ElectionState
  const [title, setTitle] = useState((init.title as string) || 'SEÇİME')
  const [targetDate, setTargetDate] = useState(toLocalInputValue(init.target_date))
  const [showCountdown, setShowCountdown] = useState(init.show_countdown !== false)
  const [showVoting, setShowVoting] = useState(init.show_voting !== false)
  const [candidates, setCandidates] = useState<Candidate[]>(() => normaliseCandidates(init.candidates))
  const [saving, setSaving] = useState(false)
  const [savedAt, setSavedAt] = useState<Date | null>(null)
  const [err, setErr] = useState('')
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({})

  function validate(): boolean {
    const errs: Record<string, string> = {}
    if (!title.trim()) errs.title = 'Başlık zorunlu'
    else if (title.length > 80) errs.title = 'Başlık 80 karakteri geçemez'
    if (!targetDate) errs.targetDate = 'Tarih zorunlu'
    candidates.forEach((c, i) => {
      if (!c.name.trim()) errs[`name-${i}`] = 'İsim zorunlu'
      else if (c.name.length > 80) errs[`name-${i}`] = 'İsim 80 karakteri geçemez'
    })
    setValidationErrors(errs)
    return Object.keys(errs).length === 0
  }

  function updateCandidate(idx: number, patch: Partial<Candidate>) {
    setCandidates((cs) => cs.map((c, i) => (i === idx ? { ...c, ...patch } : c)))
  }

  function setPercentageA(value: number) {
    const a = clampPct(value)
    const b = 100 - a
    setCandidates((cs) => [
      { ...cs[0], percentage: a },
      { ...cs[1], percentage: b },
    ])
  }

  async function save() {
    if (!validate()) {
      setErr('Lütfen formdaki hataları düzeltin')
      return
    }
    setSaving(true)
    setErr('')
    try {
      const targetISO = new Date(targetDate).toISOString()
      await api(`/api/overlays/${overlayId}/state`, {
        method: 'PATCH',
        body: JSON.stringify({
          title: title.trim(),
          target_date: targetISO,
          show_countdown: showCountdown,
          show_voting: showVoting,
          candidates,
        }),
      })
      setSavedAt(new Date())
      setValidationErrors({})
    } catch (e) {
      setErr((e as Error).message || 'Kaydedilemedi')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      <Section title="Genel">
        <Label>Başlık</Label>
        <input
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          className={`w-full border p-2 rounded ${validationErrors.title ? 'border-red-500' : ''}`}
          maxLength={80}
        />
        {validationErrors.title && <div className="text-red-600 text-xs">{validationErrors.title}</div>}

        <Label>Hedef tarih</Label>
        <input
          type="datetime-local"
          value={targetDate}
          onChange={(e) => setTargetDate(e.target.value)}
          className={`w-full border p-2 rounded ${validationErrors.targetDate ? 'border-red-500' : ''}`}
        />
        {validationErrors.targetDate && <div className="text-red-600 text-xs">{validationErrors.targetDate}</div>}

        <div className="flex gap-4 items-center mt-2">
          <Toggle label="Geri sayım göster" checked={showCountdown} onChange={setShowCountdown} />
          <Toggle label="Oylama göster" checked={showVoting} onChange={setShowVoting} />
        </div>
      </Section>

      <Section title="Adaylar">
        {candidates.map((c, idx) => (
          <CandidateBlock
            key={c.key}
            candidate={c}
            onChange={(patch) => updateCandidate(idx, patch)}
          />
        ))}

        <div className="mt-4">
          <Label>Aday A yüzdesi (B otomatik 100−A olur)</Label>
          <input
            type="range"
            min={0}
            max={100}
            value={candidates[0].percentage}
            onChange={(e) => setPercentageA(Number(e.target.value))}
            className="w-full"
          />
          <div className="text-sm text-gray-700">
            A: %{candidates[0].percentage} · B: %{candidates[1].percentage}
          </div>
        </div>
      </Section>

      {err && <div className="text-red-600 text-sm">{err}</div>}
      <div className="flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="px-4 py-2 bg-black text-white rounded disabled:opacity-50"
        >
          {saving ? 'Kaydediliyor…' : 'Kaydet'}
        </button>
        {savedAt && (
          <span className="text-xs text-gray-500">Son kayıt: {savedAt.toLocaleTimeString()}</span>
        )}
      </div>
    </div>
  )
}

function Section(props: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold">{props.title}</h2>
      <div className="space-y-2">{props.children}</div>
    </section>
  )
}

function Label(props: { children: React.ReactNode }) {
  return <label className="block text-sm font-medium mt-2">{props.children}</label>
}

function Toggle(props: { label: string; checked: boolean; onChange: (v: boolean) => void }) {
  return (
    <label className="flex items-center gap-2 text-sm">
      <input type="checkbox" checked={props.checked} onChange={(e) => props.onChange(e.target.checked)} />
      {props.label}
    </label>
  )
}

function CandidateBlock(props: { candidate: Candidate; onChange: (patch: Partial<Candidate>) => void }) {
  const c = props.candidate
  return (
    <div className="border p-3 rounded space-y-2">
      <div className="font-semibold">Aday {c.key.toUpperCase()}</div>
      <Label>İsim</Label>
      <input
        value={c.name}
        onChange={(e) => props.onChange({ name: e.target.value })}
        className="w-full border p-2 rounded"
        maxLength={80}
      />

      <Label>Foto URL (opsiyonel)</Label>
      <div className="flex gap-3 items-start">
        <input
          value={c.photo_url}
          onChange={(e) => props.onChange({ photo_url: e.target.value })}
          placeholder="https://..."
          className="flex-1 border p-2 rounded"
        />
        {c.photo_url ? (
          <img
            src={c.photo_url}
            alt=""
            className="w-16 h-16 object-cover border rounded"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = 'none'
            }}
          />
        ) : (
          <div className="w-16 h-16 border rounded bg-gray-100 flex items-center justify-center text-xs text-gray-400">
            preview
          </div>
        )}
      </div>

      <Label>Renk</Label>
      <input
        type="color"
        value={c.color}
        onChange={(e) => props.onChange({ color: e.target.value })}
        className="border p-1 rounded h-10 w-20"
      />
    </div>
  )
}

function clampPct(v: number): number {
  if (Number.isNaN(v)) return 50
  return Math.max(0, Math.min(100, Math.round(v)))
}

function normaliseCandidates(input: Candidate[]): Candidate[] {
  const findOrDefault = (key: 'a' | 'b'): Candidate => {
    const found = (input || []).find((c) => c.key === key)
    return (
      found || {
        key,
        name: key === 'a' ? 'ADAY A' : 'ADAY B',
        photo_url: '',
        color: key === 'a' ? '#163962' : '#ffed00',
        percentage: 50,
      }
    )
  }
  return [findOrDefault('a'), findOrDefault('b')]
}

function toLocalInputValue(iso: string): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (isNaN(d.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
